import React, { useEffect } from "react";
import BlogCard from "./BlogCard";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { motion, AnimatePresence } from 'framer-motion';
import { hasPermission, hasRole } from "../../utils/utils";
import BlogSkeleton from "./BlogSkeleton";
import EmptyState from "./EmptyState";
import { useBlog } from "../../hooks/useBlog";

function BlogGrid({ onPostSelect, onAddPost, activeCategoryId, searchQuery }) {
    const user = useSelector(selectUserInformations);
    const { posts, loading, fetchPosts } = useBlog();

    const canCreate = (hasPermission(user, 'create_blog_post') || (user.roles && hasRole(user.roles, 'super-admin')));

    useEffect(() => {
        fetchPosts(activeCategoryId, searchQuery);
    }, [activeCategoryId, searchQuery, fetchPosts]);

    const handleReaction = async (postId, type) => {
        // Optimistic update or just wait for refresh if needed
        // For now, we rely on the parent or detail view for deep state updates
        // but we could implement local state update here for better UX
    };

    if (loading && posts.length === 0) {
        return <BlogSkeleton />;
    }

    if (posts.length === 0 && !loading && !canCreate) {
        return <EmptyState />;
    }

    return (
        <div className="w-full">
            <motion.div
                className="container mx-auto px-4 py-6 rounded-[20px] w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 w-full">
                    <AnimatePresence mode="popLayout">
                        {/* Create Post Card */}
                        {canCreate && (
                            <motion.div
                                key="create-card"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                layout
                            >
                                <BlogCard
                                    createTopic
                                    onClick={onAddPost}
                                />
                            </motion.div>
                        )}

                        {/* Blog Post Cards */}
                        {posts.map((post) => (
                            <motion.div
                                key={post.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                layout
                            >
                                <BlogCard
                                    post={post}
                                    onReaction={handleReaction}
                                    onClick={() => onPostSelect(post.id)}
                                />
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
}

export default BlogGrid;